import { MoreVertRounded } from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  styled,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { downloadEncodedUri, useDestructiveConfirm } from "src/utilities";
import { EditMetricModal, Chart, LinkBehavior } from "..";
import { Links } from "../../constants";
import { MetricDto, useDeleteMetric } from "../../http";
import { MetricMapper } from "../../mapping";
import { ITeam } from "src/models";

const MetricLink = styled(Link)`
  color: ${({ theme }) => theme.palette.text.primary};
  display: block;
  transition: ${({ theme }) => theme.transitions.create("background-color")};

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.transparent5};
    text-decoration: none;
  }
`;

interface IProps {
  metric: MetricDto;
  metricIndex: number;
  team: ITeam;
  sx?: SxProps;
  onMetricRemoved?: (metricGuid: string) => void;
}

const TeamMetric = (props: IProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { mutate: deleteMetric } = useDeleteMetric();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useDestructiveConfirm();
  const theme = useTheme();

  function handlExportCsvClicked() {
    setMenuAnchor(null);

    const csvData = MetricMapper.toCsvData(props.metric);

    downloadEncodedUri({
      filename: `${DateTime.now().toISODate()} - ${props.metric.title}`,
      encodedUri: encodeURI(`data:text/csv;chartset=utf-8,${csvData}`),
    });
  }

  async function handleDeleteClicked() {
    setMenuAnchor(null);

    const { confirmed } = await confirm({
      title: `Delete Metric`,
      content: `Are you sure you want to delete this Metric?`,
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteMetric(
      {
        assignedTeamSlug: props.metric.assignedTeam.slug,
        guid: props.metric.guid,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(`Metric has been deleted`);
        },
      }
    );
  }

  async function handleRemoveClicked() {
    setMenuAnchor(null);

    const { confirmed } = await confirm({
      title: `Remove Metric`,
      content: `Are you sure you want to remove this Metric?`,
      confirmationText: "Remove",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    props.onMetricRemoved?.(props.metric.guid);
  }

  return (
    <>
      <MetricLink href={Links.MetricDetail(props.metric.guid)} sx={{ ...props.sx }}>
        <Stack spacing={0.5} sx={{ height: "100%" }}>
          <Stack spacing={0.5} direction="row">
            {props.metric.assignedTeam && (
              <Chip
                label={props.metric.assignedTeam.name}
                color="primary"
                component={LinkBehavior}
                href={Links.Team(props.metric.assignedTeam.slug)}
                clickable
              />
            )}
            {props.metric.reportingUser && (
              <Chip
                label={props.metric.reportingUser.displayName}
                color="wine"
                component={LinkBehavior}
                href={Links.User(props.metric.reportingUser.userId)}
                clickable
              />
            )}
          </Stack>
          <Stack
            spacing={0.5}
            direction="row"
            sx={{ display: "flex", alignItems: "start", justifyContent: "space-between" }}
          >
            <Typography variant="h5">{props.metric.title}</Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                setMenuAnchor(e.currentTarget);
              }}
            >
              <MoreVertRounded />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem onClick={handlExportCsvClicked}>Export CSV</MenuItem>
              {props.metric.userCanEdit && (
                <>
                  <MenuItem
                    onClick={() => {
                      setIsModalVisible(true);
                      setMenuAnchor(null);
                    }}
                  >
                    Update
                  </MenuItem>
                  <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
                    Delete
                  </MenuItem>
                </>
              )}
              {(props.team.isMember || props.team.isAdministrator) &&
                props.team.slug !== props.metric.assignedTeam.slug && (
                  <MenuItem sx={{ color: theme.palette.error.main }} onClick={handleRemoveClicked}>
                    Remove
                  </MenuItem>
                )}
            </Menu>
          </Stack>
          {props.metric.description && <Typography>{props.metric.description}</Typography>}
          {props.metric.datasets.length > 0 && (
            <>
              <Chart
                datasets={MetricMapper.toChartDataWithNulls(props.metric).map((data) => {
                  return {
                    title: data.title,
                    displayAsBar: data.displayAsBar,
                    color: data.color,
                    dotted: data.dotted,
                    data: data.chartData,
                    sortOrder: data.sortOrder,
                  };
                })}
                annotations={props.metric.annotations}
                sx={{ flex: 1, px: 0.5, height: "400px" }}
              />
              {props.metric.dataLastModifiedDateTimeUtc && props.metric.dataLastModifiedByDisplayName && (
                <Box>
                  <Typography variant="caption">{`Data updated by ${
                    props.metric.dataLastModifiedByDisplayName
                  } on ${DateTime.fromISO(props.metric.dataLastModifiedDateTimeUtc, { zone: "utc" })
                    .toLocal()
                    .toLocaleString(DateTime.DATE_FULL)}`}</Typography>
                </Box>
              )}
            </>
          )}
        </Stack>
      </MetricLink>

      <EditMetricModal
        isVisible={isModalVisible}
        metric={props.metric}
        onUpdated={() => setIsModalVisible(false)}
        onCancelled={() => setIsModalVisible(false)}
      />
    </>
  );
};

export { TeamMetric };
